<template>
  <!-- Nexus Navigation -->
  <router-link
    class="navbar-item nexus-nav-link"
    :class="{ 'is-active': isActiveRoute('admin-companies') }"
    :to="{ name: 'admin-companies' }"
  >
    <span
      class="icon"
      :class="{ 'has-text-primary': isActiveRoute('admin-companies') }"
    >
      <font-awesome-icon
        :icon="['fal', 'building-user']"
        :active-icon="['fadl', 'building-user']"
        :active="isActiveRoute('admin-companies')"
      />
    </span>
    <span :class="{ 'has-text-primary': isActiveRoute('admin-companies') }"
      >Companies</span
    >
  </router-link>

  <router-link
    v-if="canAccessDeliverables"
    class="navbar-item nexus-nav-link"
    :class="{ 'is-active': isActiveRoute('admin-deliverables') }"
    :to="{ name: 'admin-deliverables' }"
  >
    <span
      class="icon"
      :class="{ 'has-text-primary': isActiveRoute('admin-deliverables') }"
    >
      <font-awesome-icon
        :icon="['fal', 'chart-kanban']"
        :active-icon="['fadl', 'chart-kanban']"
        :active="isActiveRoute('admin-deliverables')"
      />
    </span>
    <span :class="{ 'has-text-primary': isActiveRoute('admin-deliverables') }"
      >Deliverables</span
    >
  </router-link>

  <router-link
    v-if="canAccessCalendar"
    class="navbar-item nexus-nav-link"
    :class="{ 'is-active': isActiveRoute('admin-calendar') }"
    :to="{ name: 'admin-calendar' }"
  >
    <span
      class="icon"
      :class="{ 'has-text-primary': isActiveRoute('admin-calendar') }"
    >
      <font-awesome-icon
        :icon="['fal', 'calendar']"
        :active-icon="['fadl', 'calendar']"
        :active="isActiveRoute('admin-calendar')"
      />
    </span>
    <span
      :class="{ 'has-text-primary': isActiveRoute('admin-calendar') }"
      >Calendar</span
    >
  </router-link>

  <router-link
    v-if="canAccessConversations"
    class="navbar-item nexus-nav-link"
    :class="{ 'is-active': isActiveRoute('admin-conversations') }"
    :to="{ name: 'admin-conversations' }"
  >
    <span
      class="icon"
      style="position: relative;"
      :class="{ 'has-text-primary': isActiveRoute('admin-conversations') }"
    >
      <font-awesome-icon
        :icon="['fal', 'message-lines']"
        :active-icon="['fadl', 'message-lines']"
        :active="isActiveRoute('admin-conversations')"
      />
      <!-- Unanswered (New) conversations waiting on the team -->
      <span v-if="newConversationCount" class="notification-dot"></span>
    </span>
    <span :class="{ 'has-text-primary': isActiveRoute('admin-conversations') }"
      >Conversations</span
    >
  </router-link>

  <!-- Reports Dropdown for Admins -->
  <div
    v-if="user && (user.type === 'super' || user.type === 'admin') && canAccessReports"
    class="navbar-item has-dropdown is-hoverable settings-dropdown nexus-nav-link"
    :class="{ 'is-active': isReportsActive }"
  >
    <a class="navbar-link settings-link">
      <span class="icon" style="position: relative;">
        <font-awesome-icon
          :icon="['fal', 'chart-line']"
          :active-icon="['fadl', 'chart-line']"
          :active="isReportsActive"
        />
        <!-- Pulsing indicator when DGF has items -->
        <span
          v-if="dgfHasItems"
          class="notification-dot"
        ></span>
      </span>
      <span>Reports</span>
    </a>
    <div class="navbar-dropdown">
      <router-link
        v-if="canAccessAssetAudit"
        :to="{ name: 'admin-asset-audit' }"
        class="navbar-item"
        :class="{
          'is-active': isActiveRoute('admin-asset-audit'),
        }"
      >
        <span class="icon is-small">
          <font-awesome-icon
            :icon="['fal', 'search']"
            :active-icon="['fadl', 'search']"
            :active="isActiveRoute('admin-asset-audit')"
          />
        </span>
        <span>Asset Audit</span>
      </router-link>
      <router-link
        v-if="canAccessUserAccessReport"
        :to="{ name: 'admin-user-access-report' }"
        class="navbar-item"
        :class="{
          'is-active': isActiveRoute('admin-user-access-report'),
        }"
      >
        <span class="icon is-small">
          <font-awesome-icon
            :icon="['fal', 'user-shield']"
            :active-icon="['fadl', 'user-shield']"
            :active="isActiveRoute('admin-user-access-report')"
          />
        </span>
        <span>User Audit</span>
      </router-link>
      <router-link
        v-if="canAccessLOAReport"
        :to="{ name: 'admin-loa-report' }"
        class="navbar-item"
        :class="{
          'is-active': isActiveRoute('admin-loa-report'),
        }"
      >
        <span class="icon is-small">
          <font-awesome-icon
            :icon="['fal', 'ghost']"
            :active-icon="['fadl', 'ghost']"
            :active="isActiveRoute('admin-loa-report')"
          />
        </span>
        <span>LOA Report</span>
      </router-link>
      <router-link
        v-if="canAccessRoute('admin-editor-stage-duration')"
        :to="{ name: 'admin-editor-stage-duration' }"
        class="navbar-item"
        :class="{
          'is-active': isActiveRoute('admin-editor-stage-duration'),
        }"
      >
        <span class="icon is-small">
          <font-awesome-icon
            :icon="['fal', 'stopwatch']"
            :active-icon="['fadl', 'stopwatch']"
            :active="isActiveRoute('admin-editor-stage-duration')"
          />
        </span>
        <span>Editor Stage Duration</span>
      </router-link>
      <router-link
        v-if="canAccessRoute('admin-msa-invoicing')"
        :to="{ name: 'admin-msa-invoicing' }"
        class="navbar-item"
        :class="{
          'is-active': isActiveRoute('admin-msa-invoicing'),
        }"
      >
        <span class="icon is-small" style="position: relative;">
          <font-awesome-icon
            :icon="['fal', 'file-invoice-dollar']"
            :active-icon="['fadl', 'file-invoice-dollar']"
            :active="isActiveRoute('admin-msa-invoicing')"
          />
          <span
            v-if="msaInvoiceCount > 0"
            class="notification-dot"
          ></span>
        </span>
        <span>MSA Invoicing</span>
        <span
          v-if="msaInvoiceCount > 0"
          class="tag is-danger is-rounded is-small ml-auto"
        >{{ msaInvoiceCount }}</span>
      </router-link>
      <router-link
        v-if="canAccessRoute('admin-podcast-publishing')"
        :to="{ name: 'admin-podcast-publishing' }"
        class="navbar-item"
        :class="{
          'is-active': isActiveRoute('admin-podcast-publishing'),
        }"
      >
        <span class="icon is-small" style="position: relative;">
          <font-awesome-icon
            :icon="['fal', 'calendar-check']"
            :active-icon="['fadl', 'calendar-check']"
            :active="isActiveRoute('admin-podcast-publishing')"
          />
          <span
            v-if="publishSchedulingCount > 0"
            class="notification-dot"
          ></span>
        </span>
        <span>Podcast Publishing</span>
        <span
          v-if="publishSchedulingCount > 0"
          class="tag is-danger is-rounded is-small ml-auto"
        >{{ publishSchedulingCount }}</span>
      </router-link>
      <router-link
        v-if="canAccessRoute('admin-delivery-risk')"
        :to="{ name: 'admin-delivery-risk' }"
        class="navbar-item"
        :class="{
          'is-active': isActiveRoute('admin-delivery-risk'),
        }"
      >
        <span class="icon is-small" style="position: relative;">
          <font-awesome-icon
            :icon="['fal', 'chart-gantt']"
            :active-icon="['fadl', 'chart-gantt']"
            :active="isActiveRoute('admin-delivery-risk')"
          />
          <span
            v-if="deliveryRiskCount > 0"
            class="notification-dot"
          ></span>
        </span>
        <span>Delivery Risk</span>
        <span
          v-if="deliveryRiskCount > 0"
          class="tag is-danger is-rounded is-small ml-auto"
        >{{ deliveryRiskCount }}</span>
      </router-link>
      <router-link
        v-if="canAccessRoute('admin-email-delivery')"
        :to="{ name: 'admin-email-delivery' }"
        class="navbar-item"
        :class="{ 'is-active': isActiveRoute('admin-email-delivery') }"
      >
        <span class="icon is-small">
          <font-awesome-icon
            :icon="['fal', 'envelope-circle-check']"
            :active-icon="['fadl', 'envelope-circle-check']"
            :active="isActiveRoute('admin-email-delivery')"
          />
        </span>
        <span>Email Delivery</span>
      </router-link>
      <router-link
        v-if="canAccessRoute('admin-dgf-report')"
        :to="{ name: 'admin-dgf-report' }"
        class="navbar-item dgf-report-item"
        :class="{
          'is-active': isActiveRoute('admin-dgf-report'),
        }"
      >
        <span class="icon is-small">
          <font-awesome-icon
            :icon="['fal', 'hand-middle-finger']"
            :active-icon="['fadl', 'hand-middle-finger']"
            :active="isActiveRoute('admin-dgf-report')"
          />
        </span>
        <span>DGF Report</span>
        <!-- DGF Counts -->
        <span v-if="dgfCounts.total > 0" class="dgf-counts ml-auto">
          <span v-if="dgfCounts.projects > 0" class="tag is-small is-warning is-rounded">
            {{ dgfCounts.projects }}
          </span>
          <span v-if="dgfCounts.deliverables > 0" class="tag is-small is-danger is-rounded">
            {{ dgfCounts.deliverables }}
          </span>
          <span v-if="dgfCounts.events > 0" class="tag is-small is-info is-rounded">
            {{ dgfCounts.events }}
          </span>
          <span v-if="dgfCounts.missingPersons > 0" class="tag is-small is-purple is-rounded">
            {{ dgfCounts.missingPersons }}
          </span>
        </span>
      </router-link>
    </div>
  </div>

  <!-- Settings Dropdown for Admins (only show if user has any settings permission) -->
  <div
    v-if="user && (user.type === 'super' || user.type === 'admin') && canAccessSettings"
    class="navbar-item has-dropdown is-hoverable settings-dropdown nexus-nav-link"
    :class="{ 'is-active': isSettingsActive }"
  >
    <a class="navbar-link settings-link">
      <span class="icon">
        <font-awesome-icon
          :icon="['fal', 'gear']"
          :active-icon="['fadl', 'gear']"
          :active="isSettingsActive"
        />
      </span>
      <span>Utilities</span>
    </a>
    <div class="navbar-dropdown">
      <!-- Tools Section. Gated on Transcription itself, not the Tools group:
           Asset Upload moved under a company, so Transcription is the only
           entry left and the group check rendered a bare heading for anyone
           holding Tools without super. -->
      <template v-if="canAccessTranscription">
        <p class="navbar-section-heading">Tools</p>
        <router-link
          v-if="canAccessTranscription"
          :to="{ name: 'admin-transcription' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-transcription'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'closed-captioning']"
              :active-icon="['fadl', 'closed-captioning']"
              :active="isActiveRoute('admin-transcription')"
            />
          </span>
          <span>Transcription</span>
        </router-link>
      </template>

      <hr v-if="canAccessTranscription && (canAccessConfig || canAccessFeedback)" class="navbar-divider" />

      <!-- Support Section -->
      <template v-if="canAccessFeedback">
        <p class="navbar-section-heading">Support</p>
        <router-link
          :to="{ name: 'admin-feedback' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-feedback'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'clipboard-list-check']"
              :active-icon="['fadl', 'clipboard-list-check']"
              :active="isActiveRoute('admin-feedback')"
            />
          </span>
          <span>Issue Tracker</span>
        </router-link>
      </template>

      <hr v-if="canAccessFeedback && canAccessAutomation" class="navbar-divider" />

      <!-- Automation Section -->
      <template v-if="canAccessAutomation">
        <p class="navbar-section-heading">Automation</p>
        <router-link
          :to="{ name: 'admin-automation-rules' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-automation-rules'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'robot']"
              :active-icon="['fadl', 'robot']"
              :active="isActiveRoute('admin-automation-rules')"
            />
            <!-- Pulsing indicator when automation rules have errors -->
            <span
              v-if="hasAutomationErrors"
              class="notification-dot automation-error-dot"
              :title="`${automationErrorCount} automation error${automationErrorCount !== 1 ? 's' : ''}`"
            ></span>
          </span>
          <span>Deliverable Rules</span>
        </router-link>
        <router-link
          v-if="canAccessRoute('admin-workpool-settings')"
          :to="{ name: 'admin-workpool-settings' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-workpool-settings'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'inbox']"
              :active-icon="['fadl', 'inbox']"
              :active="isActiveRoute('admin-workpool-settings')"
            />
          </span>
          <span>Work Pool Settings</span>
        </router-link>
      </template>

      <hr v-if="canAccessAutomation && (canAccessAdminManagement || canAccessFormBuilder)" class="navbar-divider" />

      <!-- Config Section -->
      <template v-if="canAccessAdminManagement || canAccessFormBuilder || canAccessRoute('admin-oauth-apps')">
        <p class="navbar-section-heading">Config</p>
        <router-link
          v-if="canAccessAdminManagement"
          :to="{ name: 'admin-users' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-users'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'users']"
              :active-icon="['fadl', 'users']"
              :active="isActiveRoute('admin-users')"
            />
          </span>
          <span>Admin Management</span>
        </router-link>
        <router-link
          v-if="canAccessFormBuilder"
          :to="{ name: 'admin-form-builder' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-form-builder'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'clipboard-list']"
              :active-icon="['fadl', 'clipboard-list']"
              :active="isActiveRoute('admin-form-builder')"
            />
          </span>
          <span>Form Builder</span>
        </router-link>
        <router-link
          v-if="canAccessRoute('admin-pricing-config')"
          :to="{ name: 'admin-pricing-config' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-pricing-config'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'tags']"
              :active-icon="['fadl', 'tags']"
              :active="isActiveRoute('admin-pricing-config')"
            />
          </span>
          <span>Pricing Config</span>
        </router-link>
        <router-link
          v-if="canAccessRoute('admin-scheduling-rules')"
          :to="{ name: 'admin-scheduling-rules' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-scheduling-rules'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'calendar-check']"
              :active-icon="['fadl', 'calendar-check']"
              :active="isActiveRoute('admin-scheduling-rules')"
            />
          </span>
          <span>Scheduling Rules</span>
        </router-link>
        <router-link
          v-if="canAccessRoute('admin-oauth-apps')"
          :to="{ name: 'admin-oauth-apps' }"
          class="navbar-item"
          :class="{
            'is-active': isActiveRoute('admin-oauth-apps'),
          }"
        >
          <span class="icon is-small">
            <font-awesome-icon
              :icon="['fal', 'plug']"
              :active-icon="['fadl', 'plug']"
              :active="isActiveRoute('admin-oauth-apps')"
            />
          </span>
          <span>OAuth Apps</span>
        </router-link>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from 'vue'
import { subscribeNewCount } from '@/services/conversationService'
import { useNavbarState } from '@/composables/useNavbarState'
import { usePermissions } from '@/composables/usePermissions'
import { useMsaInvoicingStore } from '@/stores/msaInvoicingStore'
import { usePublishSchedulingStore } from '@/stores/publishSchedulingStore'
import { useDeliveryRiskStore } from '@/stores/deliveryRiskStore'

const {
  user,
  isActiveRoute,
  isReportsActive,
  isSettingsActive,
  dgfCounts,
  dgfHasItems,
  hasAutomationErrors,
  automationErrorCount,
} = useNavbarState()

const { canAccessRoute, canAccessGroup } = usePermissions()

// Navigation visibility — derived from route-based permissions
const canAccessDeliverables = computed(() => canAccessRoute('admin-deliverables'))
const canAccessCalendar = computed(() => canAccessRoute('admin-calendar'))
const canAccessReports = computed(() => canAccessGroup('Reports'))
const canAccessTranscription = computed(() => canAccessRoute('admin-transcription'))
const canAccessSettings = computed(() =>
  // Tools' only remaining item is Transcription (super-only), so the group
  // check would open an empty dropdown.
  canAccessTranscription.value || canAccessGroup('Reports') || canAccessGroup('Configuration')
)
const canAccessFeedback = computed(() => canAccessRoute('admin-feedback'))
const canAccessConversations = computed(() => canAccessRoute('admin-conversations'))

const newConversationCount = ref(0)
let stopNewCount = null
watch(canAccessConversations, (can) => {
  stopNewCount?.()
  stopNewCount = can ? subscribeNewCount((n) => { newConversationCount.value = n }) : null
}, { immediate: true })
onUnmounted(() => stopNewCount?.())
const canAccessAssetAudit = computed(() => canAccessRoute('admin-asset-audit'))
const canAccessUserAccessReport = computed(() => canAccessRoute('admin-user-access-report'))
const canAccessLOAReport = computed(() => canAccessRoute('admin-loa-report'))
const canAccessAdminManagement = computed(() => canAccessRoute('admin-users'))
const canAccessFormBuilder = computed(() => canAccessRoute('admin-form-builder'))
const canAccessAutomation = computed(() => canAccessRoute('admin-automation-rules'))
const canAccessConfig = computed(() => canAccessGroup('Configuration'))

// MSA Invoicing badge — live count of payments awaiting an invoice
const canAccessMsaInvoicing = computed(() => canAccessRoute('admin-msa-invoicing'))
const msaInvoicingStore = useMsaInvoicingStore()
if (canAccessMsaInvoicing.value) {
  msaInvoicingStore.init()
}
const msaInvoiceCount = computed(() => msaInvoicingStore.actionCount)

// Podcast Publishing badge — live count of episodes still needing scheduling
const canAccessPublishScheduling = computed(() => canAccessRoute('admin-podcast-publishing'))
const publishSchedulingStore = usePublishSchedulingStore()
if (canAccessPublishScheduling.value) {
  publishSchedulingStore.init()
}
const publishSchedulingCount = computed(() => publishSchedulingStore.actionCount)

// Delivery Risk badge — late + at-risk deliverables from the hourly server compute
const deliveryRiskStore = useDeliveryRiskStore()
if (canAccessRoute('admin-delivery-risk')) {
  deliveryRiskStore.init()
}
const deliveryRiskCount = computed(() => deliveryRiskStore.actionCount)
</script>

<style lang="scss" scoped>
// Top-level Nexus links: smaller icons, tighter icon-label gap (same as the
// client nav). Doubled class beats navBar.vue's `.navbar .navbar-item { gap }`
// and the shared `.settings-link { gap !important }`.
.nexus-nav-link.nexus-nav-link {
  gap: 0.25rem;

  &.settings-dropdown .settings-link {
    gap: 0.25rem !important;
  }

  > .icon,
  > .settings-link > .icon {
    width: 1.25rem;
    font-size: 0.85rem;
  }
}

@import '@/assets/scss/navbar-shared';

// DGF Report item with counts (AdminDesktopNav-specific)
.settings-dropdown .dgf-report-item {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;

  .dgf-counts {
    display: flex;
    gap: 0.25rem;
    margin-left: auto;

    .tag {
      font-size: 0.65rem;
      padding: 0.15rem 0.4rem;
      height: auto;
      min-width: 1.25rem;
      justify-content: center;
    }
  }
}
</style>
